import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { STATE_BY_SLUG, addressInState } from './ctStatePages';

const { LatLng, LatLngBounds } = sdkTypes;

const PER_PAGE = 100;

const fetchStateListingsPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { slug, config } = arg;
  const state = STATE_BY_SLUG[slug];
  if (!state) {
    return { slug, listingIds: [] };
  }
  const { neLat, neLng, swLat, swLng } = state.bounds;
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  try {
    const response = await sdk.listings.query({
      bounds: new LatLngBounds(new LatLng(neLat, neLng), new LatLng(swLat, swLng)),
      perPage: PER_PAGE,
      page: 1,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.categoryLevel1',
        'publicData.location',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        'publicData.verifieddealer',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.metadata'],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-small',
        'variants.scaled-medium',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    });
    dispatch(addMarketplaceEntities(response));
    // Bounds are rectangles, so keep only listings whose address is in this state
    const listingIds = response.data.data
      .filter(l => addressInState(l.attributes.publicData?.location?.address, state))
      .map(l => l.id);
    return { slug, listingIds };
  } catch (e) {
    log.error(e, 'state-page-listings-failed', { slug });
    return rejectWithValue(storableError(e));
  }
};

export const fetchStateListings = createAsyncThunk(
  'StatePage/fetchStateListings',
  fetchStateListingsPayloadCreator
);

const initialState = {
  slug: null,
  listingIds: [],
  inProgress: false,
  error: null,
};

const statePageSlice = createSlice({
  name: 'StatePage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchStateListings.pending, (state, action) => {
        state.inProgress = true;
        state.error = null;
        if (state.slug !== action.meta.arg.slug) {
          state.slug = action.meta.arg.slug;
          state.listingIds = [];
        }
      })
      .addCase(fetchStateListings.fulfilled, (state, action) => {
        state.inProgress = false;
        state.slug = action.payload.slug;
        state.listingIds = action.payload.listingIds;
      })
      .addCase(fetchStateListings.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload || null;
      });
  },
});

export default statePageSlice.reducer;

export const loadData = (params, search, config) => dispatch =>
  dispatch(fetchStateListings({ slug: params.state, config }));
